import { useEffect, useRef } from "react";
import EmotionIndicatorBadge from "../EmotionIndicatorBadge";
import { styles } from "./eieStyles";

export function EIEProductionView({
  isCalibrationComplete,
  badgeEmotion,
  state,
  heartRateBpm,
  isActive,
  badgeCalibrating,
  badgeAccent,
  showProductionCalibrationOverlay,
  calibrationWindowPalette,
  productionCalibrationVisible,
  showVideoOverlay,
  introVideoRef,
  activeCalibrationAccent,
  showUnmuteCTA,
  setShowUnmuteCTA,
  setVideoAudioBlocked,
  telemetryRowsRef,
  captureSessionIdRef,
  calibrationPercent,
  calibrationFaceDetected = true,
  openCalibrationPanel,
  productionPrivacyAccepted,
  setProductionPrivacyAccepted,
  onPrivacyAcceptedStart,
  onSkipIntro,
  productionFlowStarted,
  enableRemoteCsvExport,
  downloadTelemetryCsv,
  telemetryCount,
  clearTelemetry,
  captureSessionId,
  rotateCaptureSession,
  stop,
  videoRef,
}) {
  const lastBadgeStateRef = useRef({ cognitive: null, visible: null });

  useEffect(() => {
    if (!telemetryRowsRef) return;
    const cognitive = state?.cognitive ?? null;
    const visible = isCalibrationComplete && (isActive || badgeCalibrating);
    const last = lastBadgeStateRef.current;
    if (last.cognitive === cognitive && last.visible === visible) return;
    lastBadgeStateRef.current = { cognitive, visible };

    // Log transitions to/from "No Face Detected" and visibility changes.
    if (cognitive === 'out_of_frame' || last.cognitive === 'out_of_frame' || visible !== last.visible) {
      try {
        telemetryRowsRef.current.push({
          ts: Date.now(),
          event: 'badge_overlay',
          sessionId: captureSessionIdRef?.current ?? null,
          badgeVisible: visible,
          badgeCognitive: cognitive,
          badgeLabel: cognitive === 'out_of_frame' ? 'No Face Detected' : (state?.emotion || null),
          winner: state?.emotion ?? null,
          winnerScore: state?.confidence ?? null,
          facJson: state?.blendshapes ? JSON.stringify(state.blendshapes) : undefined,
        });
      } catch (e) {
        // ignore telemetry errors
      }
    }
  }, [state?.cognitive, state?.emotion, state?.confidence, isCalibrationComplete, isActive, badgeCalibrating, telemetryRowsRef, captureSessionIdRef]);
  return (
    <>
      {isCalibrationComplete ? (
        <EmotionIndicatorBadge
          emotion={badgeEmotion}
          confidence={state?.confidence}
          cognitive={state?.cognitive}
          heartRateBpm={heartRateBpm}
          isDetecting={isActive || badgeCalibrating}
          activeColor={badgeAccent}
          isCalibrating={false}
          calibrationPercent={100}
        />
      ) : null}
      {showProductionCalibrationOverlay ? (
        <div
          style={{
            ...styles.productionCalibrationOverlay,
            background: `radial-gradient(132% 122% at 50% 58%, rgba(2,6,23,0.06), ${calibrationWindowPalette.surfaceA} 54%, ${calibrationWindowPalette.surfaceB} 100%), linear-gradient(180deg, rgba(3,8,20,0.32), rgba(2,6,23,0.74))`,
            opacity: productionCalibrationVisible ? 1 : 0,
            transition: 'opacity 620ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
          aria-live="polite"
        >
          <style>{`@keyframes eieProdPulse { 0% { transform: scale(0.96); opacity: 0.92; } 50% { transform: scale(1.04); opacity: 1; } 100% { transform: scale(0.96); opacity: 0.92; } }
@keyframes eieProdRipple { 0% { transform: scale(0.65); opacity: 0.55; } 100% { transform: scale(1.65); opacity: 0; } }`}</style>
            {showVideoOverlay ? (
              <div className="eie-prod-video-card" style={{
                ...styles.productionVideoCard,
                width: 'min(92vw, 840px)',
                height: 'min(78vh, 520px)',
                border: `1px solid ${calibrationWindowPalette.border}`,
                background: `linear-gradient(145deg, ${calibrationWindowPalette.surfaceA}, ${calibrationWindowPalette.surfaceB})`,
                boxShadow: `0 32px 78px rgba(2,6,23,0.58), inset 0 1px 0 ${calibrationWindowPalette.soft}`,
              }}>
                <video
                  ref={introVideoRef}
                  playsInline
                  preload="metadata"
                  src={encodeURI('/assets/videos/EIE-Intro Guide-web.mp4')}
                  style={styles.productionIntroVideo}
                />

                <div id="calibration-target" style={{
                  ...styles.productionTargetOverlay,
                  border: `1px solid ${activeCalibrationAccent}55`,
                  boxShadow: `0 20px 44px rgba(2,6,23,0.46), 0 0 0 12px ${activeCalibrationAccent}22`,
                  display: 'none',
                }}>
                  <div style={{ ...styles.productionTargetInner, borderColor: `${activeCalibrationAccent}99` }}>
                    <span style={styles.productionTargetE}>E</span>
                    <span style={styles.productionTargetIE}>IE</span>
                  </div>
                </div>

                {showUnmuteCTA && (
                  <button
                    type="button"
                    aria-label="Enable sound for intro"
                    className="eie-prod-unmute-cta"
                    style={{
                      ...styles.productionUnmuteCTA,
                      border: `1px solid ${activeCalibrationAccent}88`,
                      background: `linear-gradient(145deg, ${activeCalibrationAccent}bb, ${activeCalibrationAccent}77)`,
                      color: calibrationWindowPalette.text,
                    }}
                    onClick={async () => {
                      try {
                        if (introVideoRef.current) {
                          introVideoRef.current.muted = false;
                          await introVideoRef.current.play();
                          setShowUnmuteCTA(false);
                          setVideoAudioBlocked(false);
                          telemetryRowsRef.current.push({ ts: Date.now(), event: 'video_unmuted', sessionId: captureSessionIdRef.current, source: 'video-calibration' });
                        }
                      } catch (err) {
                        console.warn('unmute failed', err);
                      }
                    }}
                  >
                    Tap for sound
                  </button>
                )}

                <div className="eie-prod-video-controls" style={styles.productionVideoControls}>
                  <button
                    type="button"
                    style={{
                      ...styles.productionControlPrimary,
                      border: `1px solid ${activeCalibrationAccent}99`,
                      background: `linear-gradient(145deg, ${activeCalibrationAccent}cc, ${activeCalibrationAccent}88)`,
                    }}
                    onClick={async () => {
                      try {
                        if (introVideoRef.current) {
                          introVideoRef.current.currentTime = 0;
                          await introVideoRef.current.play();
                          telemetryRowsRef.current.push({ ts: Date.now(), event: 'video_replay', sessionId: captureSessionIdRef.current, source: 'video-calibration' });
                        }
                      } catch (err) {
                        console.warn('replay failed', err);
                      }
                    }}
                  >
                    Replay Intro
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.productionControlSecondary,
                      border: `1px solid ${calibrationWindowPalette.border}`,
                      background: `linear-gradient(145deg, ${calibrationWindowPalette.surfaceA}, rgba(15,23,42,0.62))`,
                      color: calibrationWindowPalette.text,
                    }}
                    onClick={onSkipIntro}
                  >
                    Skip Intro
                  </button>
                </div>

                <div className="eie-prod-progress-inline" style={{
                  ...styles.productionProgressInline,
                  border: `1px solid ${activeCalibrationAccent}88`,
                  color: calibrationWindowPalette.text,
                  background: `linear-gradient(145deg, ${calibrationWindowPalette.surfaceA}, rgba(2,6,23,0.72))`,
                }}>
                  {typeof calibrationPercent === 'number'
                    ? (calibrationPercent >= 100 ? 'Calibration complete ✓' : `Calibrating… ${calibrationPercent}%`)
                    : 'Preparing camera...'}
                </div>
              </div>
            ) : (
              <div style={{
                ...styles.productionPromptCard,
                border: `1px solid ${calibrationWindowPalette.border}`,
                background: `linear-gradient(145deg, ${calibrationWindowPalette.surfaceA}, ${calibrationWindowPalette.surfaceB})`,
              }}>
                <div style={{ ...styles.productionRipple, borderColor: `${badgeAccent}66`, animationDelay: '0ms' }} />
                <div style={{ ...styles.productionRipple, borderColor: `${badgeAccent}44`, animationDelay: '700ms' }} />
                <div style={{ ...styles.productionRipple, borderColor: `${badgeAccent}33`, animationDelay: '1400ms' }} />
                <div style={{ ...styles.productionCore, boxShadow: `0 0 0 18px ${badgeAccent}22, 0 0 58px ${badgeAccent}66` }}>
                  <div style={styles.productionBrandRow}>
                    <span style={styles.productionBrandE}>E</span>
                    <span style={styles.productionBrandIE}>IE</span>
                  </div>
                </div>

                <div style={{ ...styles.productionTitle, color: calibrationWindowPalette.text }}>{productionPrivacyAccepted ? 'EIE starting' : 'Accept to continue'}</div>

                {!productionPrivacyAccepted ? (
                  <div style={{
                    ...styles.productionPrivacyNotice,
                    border: `1px solid ${calibrationWindowPalette.border}`,
                    background: `linear-gradient(145deg, ${calibrationWindowPalette.surfaceA}, rgba(15,23,42,0.44))`,
                  }}>
                    <strong>Your privacy, always.</strong> Your experience remains entirely local—no data collection, no external sharing.
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: calibrationWindowPalette.text }}>
                        <input
                          type="checkbox"
                          checked={productionPrivacyAccepted}
                          onChange={async (e) => {
                            const checked = e.target.checked;
                            setProductionPrivacyAccepted(checked);
                            if (checked) {
                              await onPrivacyAcceptedStart?.();
                            }
                          }}
                        />
                        I read and accept to continue
                      </label>
                      <button
                        type="button"
                        style={{
                          ...styles.productionCancelButton,
                          borderColor: calibrationWindowPalette.border,
                          color: calibrationWindowPalette.text,
                        }}
                        onClick={stop}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ ...styles.productionProgress, color: activeCalibrationAccent, transition: 'color 1.8s ease' }}>
                      {typeof calibrationPercent === 'number'
                        ? (calibrationPercent >= 100 ? 'Calibration complete ✓' : `Calibrating… ${calibrationPercent}%`)
                        : 'Preparing camera...'}
                    </div>
                    <div style={{ ...styles.productionHint, color: calibrationFaceDetected ? calibrationWindowPalette.text : activeCalibrationAccent }}>
                      {calibrationFaceDetected
                        ? 'Look at the center while we calibrate your emotional twin.'
                        : (
                          <>
                            <svg width="48" height="20" viewBox="0 0 48 20" fill="none" style={{
                              display: 'block',
                              margin: '0 auto 6px',
                              filter: `drop-shadow(0 0 6px ${activeCalibrationAccent})`,
                              animation: 'eieEyePulse 1.2s ease-in-out infinite',
                            }}>
                              <ellipse cx="12" cy="10" rx="10" ry="7" stroke={activeCalibrationAccent} strokeWidth="1.5" fill="none" />
                              <circle cx="12" cy="10" r="3" fill={activeCalibrationAccent} />
                              <ellipse cx="36" cy="10" rx="10" ry="7" stroke={activeCalibrationAccent} strokeWidth="1.5" fill="none" />
                              <circle cx="36" cy="10" r="3" fill={activeCalibrationAccent} />
                            </svg>
                            <span style={{ fontSize: 11, opacity: 0.85 }}>We can't see you — look at the screen</span>
                            <style>{`@keyframes eieEyePulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }`}</style>
                          </>
                        )}
                    </div>

                    <div style={{
                      ...styles.productionReplayBar,
                      border: `1px solid ${calibrationWindowPalette.border}`,
                      background: `linear-gradient(145deg, ${calibrationWindowPalette.soft}, rgba(15,23,42,0.24))`,
                    }}>
                      <div style={styles.productionReplayMeta}>
                        <span style={styles.productionReplayKicker}>Guide</span>
                        <span style={styles.productionReplayLabel}>Want a quick refresher while calibration runs?</span>
                      </div>
                      <button
                        type="button"
                        style={{
                          ...styles.productionReplayButton,
                          borderColor: `${activeCalibrationAccent}88`,
                          backgroundColor: `${activeCalibrationAccent}99`,
                          color: calibrationWindowPalette.text,
                          transition: 'border-color 1.8s ease, background-color 1.8s ease',
                        }}
                        onClick={async () => {
                          telemetryRowsRef.current.push({ ts: Date.now(), event: 'video_replay_prompt_clicked', sessionId: captureSessionIdRef.current, source: 'video-calibration' });
                          await openCalibrationPanel(true);
                        }}
                      >
                        Play Guide
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
        </div>
      ) : null}
      {productionFlowStarted && isCalibrationComplete ? (
        <button
          type="button"
          onClick={stop}
          style={{
            ...styles.productionExitButton,
            borderColor: `${badgeAccent}44`,
            background: `linear-gradient(145deg, ${badgeAccent}cc, ${badgeAccent}88)`,
            boxShadow: `0 16px 40px rgba(2,6,23,0.5), 0 0 20px ${badgeAccent}33`,
            transition: 'transform 180ms ease, box-shadow 560ms ease, border-color 560ms ease, background 560ms ease',
          }}
          aria-label="Stop EIE experience"
        >
          End Experience
        </button>
      ) : null}
      {enableRemoteCsvExport ? (
        <div
          style={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            zIndex: 10000,
            width: 'min(92vw, 380px)',
            padding: '12px',
            borderRadius: 12,
            border: `1px solid ${calibrationWindowPalette.border}`,
            background: `linear-gradient(145deg, ${calibrationWindowPalette.surfaceA}, ${calibrationWindowPalette.surfaceB})`,
            boxShadow: '0 18px 42px rgba(2,6,23,0.52)',
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: 1.1, textTransform: 'uppercase', color: '#94a3b8' }}>Remote Test Capture</div>
          <div style={{ marginTop: 6, fontSize: 12, color: calibrationWindowPalette.text, wordBreak: 'break-all' }}>
            Session: {captureSessionId}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={downloadTelemetryCsv}
              disabled={telemetryCount === 0}
              style={telemetryCount ? styles.btnLog : styles.btnLogDisabled}
            >
              Download Test Log CSV ({telemetryCount})
            </button>
            <button
              type="button"
              onClick={clearTelemetry}
              disabled={telemetryCount === 0}
              style={telemetryCount ? styles.btnClear : styles.btnLogDisabled}
            >
              Clear Log
            </button>
            <button
              type="button"
              onClick={rotateCaptureSession}
              style={styles.btnClear}
            >
              New Session ID
            </button>
          </div>
        </div>
      ) : null}
      <video ref={videoRef} autoPlay playsInline muted style={styles.hiddenVideo} />
    </>
  );
}
